// client/src/pages/InquiryPage.js
// 문의하기 — v5 리디자인 (문의 종류 카드 그리드 + FAQ·가이드 안내 카드)
// 로그인 가드·역할 확인·접수 로직은 기존 그대로.
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import InfluencerHomeHeader from '../components/influencer/InfluencerHomeHeader';
import MarketerHomeHeader from '../components/marketer/MarketerHomeHeader';
import { PointProvider } from '../contexts/PointContext';
import '../styles/public/pub.css';

const KINDS = [
  { value: 'point', icon: '💰', label: '포인트 / 결제' },
  { value: 'project', icon: '📦', label: '프로젝트 진행' },
  { value: 'profile', icon: '👤', label: '회원 정보' },
  { value: 'ad-agency', icon: '🤝', label: '광고 대행 문의' },
  { value: 'report', icon: '🚨', label: '신고하기' },
  { value: 'etc', icon: '💬', label: '기타' },
];

function InquiryPage() {
  const location = useLocation();
  const hintedRole = location.state?.role || null;

  const [role, setRole] = useState(hintedRole);
  const nav = useNavigate();

  // 폼 상태
  const [name, setName] = useState('');
  const [contact, setContact] = useState('');
  const [kind, setKind] = useState('');
  const [content, setContent] = useState('');
  const [open, setOpen] = useState(false);
  const [msg, setMsg] = useState('');
  const [loading, setLoading] = useState(false);

  const remain = useMemo(() => 2000 - String(content || '').length, [content]);
  const canSubmit = useMemo(() => {
    return !!name.trim() && !!contact.trim() && !!kind && !!content.trim() && remain >= 0 && !loading;
  }, [name, contact, kind, content, remain, loading]);

  // 기존 가드/역할 확인 로직 유지
  useEffect(() => {
    let ignore = false;
    (async () => {
      try {
        const res = await fetch('/api/user/me', { credentials: 'include' });

        if (res.status === 401) {
          if (!ignore) nav('/');
          return;
        }

        if (!res.ok) {
          return;
        }

        const data = await res.json().catch(() => null);
        const serverRole = data?.role;
        if (!ignore) {
          if (serverRole === 'influencer' || serverRole === 'marketer') {
            if (role !== serverRole) setRole(serverRole);
          } else {
            nav('/');
            return;
          }
        }
      } catch {
        // 일시적 오류 시 화면 잔류
      }
    })();
    return () => { ignore = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nav]);

  const onSubmit = async () => {
    if (!canSubmit) return;
    setLoading(true);
    setMsg('');
    try {
      const res = await fetch('/api/inquiry', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, contact, kind, content }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data?.ok) {
        setOpen(true);
        setName(''); setContact(''); setKind(''); setContent('');
      } else {
        setMsg(data?.message || '문의 접수에 실패했습니다.');
      }
    } catch {
      setMsg('서버 요청 실패');
    }
    setLoading(false);
  };

  return (
    <div className="pub">
      {role === 'influencer' ? (
        <PointProvider userRole="influencer">
          <InfluencerHomeHeader />
        </PointProvider>
      ) : role === 'marketer' ? (
        <PointProvider userRole="marketer">
          <MarketerHomeHeader />
        </PointProvider>
      ) : null}

      <main className="wrap wrap--narrow">
        <div className="pub-head">
          <h1>문의하기</h1>
          <p>남겨주신 문의는 순차적으로 확인 후 등록된 메일 또는 연락처로 답변드려요.</p>
        </div>

        <div className="card">
          <h3>어떤 문의인가요?</h3>
          <div className="kindgrid">
            {KINDS.map((k) => (
              <button
                key={k.value}
                type="button"
                className={`kindcard${kind === k.value ? ' is-active' : ''}`}
                onClick={() => setKind(k.value)}
                disabled={loading}
              >
                <i aria-hidden="true">{k.icon}</i>{k.label}
              </button>
            ))}
          </div>

          <div className="grid2">
            <div className="field" style={{ margin: 0 }}>
              <label>이름<span className="req">*</span></label>
              <input
                value={name}
                onChange={e=>setName(e.target.value)}
                placeholder="답변 받으실 분의 이름"
                disabled={loading}
              />
            </div>
            <div className="field" style={{ margin: 0 }}>
              <label>연락처<span className="req">*</span></label>
              <input
                value={contact}
                onChange={e=>setContact(e.target.value)}
                placeholder="휴대폰 번호 또는 이메일"
                disabled={loading}
              />
            </div>
          </div>

          <div className="field" style={{ marginTop: 14 }}>
            <label>문의 내용<span className="req">*</span></label>
            <textarea
              value={content}
              onChange={e=>setContent(e.target.value)}
              placeholder="문의하실 내용을 자세히 적어주세요. 프로젝트 관련 문의라면 프로젝트 번호(NO.)를 함께 적어주시면 빨라요."
              maxLength={2000}
              rows={8}
              disabled={loading}
            />
            <div className="hint" style={{ textAlign: 'right', color: remain < 0 ? '#d13b3b' : undefined }}>
              {Math.max(0, remain)} / 2000
            </div>
          </div>

          {msg && <div className="note note--red" style={{ marginTop: 0, marginBottom: 14 }}>{msg}</div>}

          <button
            type="button"
            className="btn btn--blue btn--full btn--lg"
            onClick={onSubmit}
            disabled={!canSubmit}
          >
            {loading ? '접수 중...' : '문의 접수하기'}
          </button>
        </div>

        <div className="card">
          <h3>잠깐, 이미 답이 있을 수도 있어요</h3>
          <div className="checkline">
            <i>?</i>
            <div>
              <b>충전·환전이 궁금하다면</b>
              <span>FAQ 포인트·정산 항목에서 바로 확인할 수 있어요</span>
            </div>
          </div>
          <div className="checkline">
            <i>?</i>
            <div>
              <b>진행 방법이 궁금하다면</b>
              <span>역할별 서비스 가이드에 전 과정이 정리되어 있어요</span>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 9, marginTop: 12 }}>
            <button type="button" className="btn btn--ghost" onClick={() => nav('/faq')}>FAQ 보기</button>
            <button
              type="button"
              className="btn btn--ghost"
              onClick={() => window.open('/guide', '_blank', 'noopener,noreferrer')}
            >
              서비스 가이드
            </button>
          </div>
        </div>
      </main>

      {open && (
        <div className="pub-modal" onClick={() => setOpen(false)}>
          <div className="pub-modal__box" onClick={(e) => e.stopPropagation()}>
            <p>문의가 접수되었습니다.<br />담당자가 확인 후 연락드릴게요.</p>
            <button type="button" className="btn btn--blue btn--full" onClick={() => setOpen(false)}>
              확인
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default InquiryPage;
